<script setup>
import { useRoute, useRouter } from 'vue-router'
import { ref, computed, watch, onMounted } from 'vue'
import { useOrganizationStore } from '@/stores/organizationStore'
import { useToastStore } from '@/stores/toastStore'
import DeleteModal from '@/components/DeleteModal.vue'

const route = useRoute()
const router = useRouter()
const orgStore = useOrganizationStore()
const toastStore = useToastStore()
const organizationId = route.params.id

// Form State
const editableName = ref('')
const isSaving = ref(false)
const saveMessage = ref('')
const saveError = ref('')

// Delete State
const isDeleteAcknowledged = ref(false)
const isDeleting = ref(false)
const deleteError = ref('')

// --- API Key State ---
const isCreatingKey = ref(false)
const apiKeyMessage = ref('')
const apiKeyError = ref('')
const generatedKey = ref(null)

const expiryOptions = ['1d', '7d', '30d', '90d']
const selectedExpiry = ref('30d')
const selectedScopes = ref([])

// --- OAuth Application State ---
const oauthApps = ref([])
const isFetchingApps = ref(false)
const isCreatingApp = ref(false)
const oauthAppMessage = ref('')
const oauthAppError = ref('')
const newAppName = ref('')
const _nameRegex = /^[a-z][a-z0-9-]{1,31}$/
const newAppDescription = ref('')
const newAppRedirectURIs = ref('')
const createdAppCredentials = ref(null) // shows { clientId, clientSecret } once
const newAppSelectedScopes = ref([])
const deletingAppId = ref(null)

// --- Delete Modal State ---
// pendingDelete: { type: 'org' } | { type: 'oauthApp', app: {...} } | null
const pendingDelete = ref(null)
const isDeleteModalVisible = computed(() => pendingDelete.value !== null)
const deleteModalItemType = computed(() => pendingDelete.value?.type === 'org' ? 'Organization' : 'OAuth Application')
const deleteModalItemName = computed(() => pendingDelete.value?.type === 'org' ? organization.value?.name : pendingDelete.value?.app?.name)

const scopeGroups = [
  {
    label: 'Variables',
    prefix: 'variable',
    actions: ['create', 'read', 'update', 'delete', 'list']
  },
  {
    label: 'Providers',
    prefix: 'provider',
    actions: ['create', 'read', 'update', 'delete', 'list']
  },
  {
    label: 'Servers',
    prefix: 'server',
    actions: ['create', 'read', 'update', 'delete', 'list', 'create_token']
  }
]

// Toggle logic for scopes
const toggleScope = (scope) => {
  if (selectedScopes.value.includes(scope)) {
    selectedScopes.value = selectedScopes.value.filter(s => s !== scope)
  } else {
    selectedScopes.value.push(scope)
  }
}

// Fetch orgs if missing (e.g. on direct page load)
onMounted(() => {
  if (orgStore.organizations.length === 0) {
    orgStore.fetchOrganizations()
  }
  fetchOAuthApps()
})

const fetchOAuthApps = async () => {
  isFetchingApps.value = true
  try {
    oauthApps.value = await orgStore.listOAuthApplications(organizationId)
  } catch (err) {
    console.error('Failed to fetch OAuth apps', err)
  } finally {
    isFetchingApps.value = false
  }
}

// Compute the current organization from the store
const organization = computed(() => orgStore.getOrganizationById(organizationId))

// Sync local input with store data
watch(organization, (newOrg) => {
  if (newOrg && newOrg.name) {
    editableName.value = newOrg.name
  }
}, { immediate: true })

const isNameValid = computed(() => {
  return editableName.value && editableName.value.trim().length > 1
})

const handleSave = async () => {
  if (!isNameValid.value) return

  isSaving.value = true
  saveMessage.value = ''
  saveError.value = ''

  try {
    await orgStore.updateOrganization(organizationId, editableName.value)
    saveMessage.value = 'Organization settings updated.'
  } catch (err) {
    saveError.value = err.response?.data?.error?.message || 'Failed to update organization.'
  } finally {
    isSaving.value = false
    setTimeout(() => { saveMessage.value = '' }, 3000)
  }
}

const handleCreateKey = async () => {
  isCreatingKey.value = true
  apiKeyMessage.value = ''
  apiKeyError.value = ''
  generatedKey.value = null

  try {
    // Calculate expiresAt
    const now = new Date()
    let days = 30
    switch (selectedExpiry.value) {
      case '1d': days = 1; break;
      case '7d': days = 7; break;
      case '30d': days = 30; break;
      case '90d': days = 90; break;
      case '1y': days = 365; break;
    }
    const expiresAt = new Date(now.getTime() + days * 24 * 60 * 60 * 1000).toISOString()

    const payload = {
      key: {
        expiresAt: expiresAt,
        scopes: selectedScopes.value
      }
    }

    const keyData = await orgStore.createApiKey(organizationId, payload)
    generatedKey.value = keyData
    toastStore.showToast('API key created successfully.')
  } catch (err) {
    toastStore.showToast(err.response?.data?.error?.message || 'Failed to create API key.', 'error')
  } finally {
    isCreatingKey.value = false
  }
}

const handleCopyKey = async () => {
  if (!generatedKey.value?.accessToken) return

  try {
    await navigator.clipboard.writeText(generatedKey.value.accessToken)
    apiKeyMessage.value = 'Copied to clipboard!'
    // Hide the key after a short delay
    setTimeout(() => {
      generatedKey.value = null
      apiKeyMessage.value = ''
      // Reset form
      selectedScopes.value = []
      selectedExpiry.value = '30d'
    }, 1000)
  } catch (err) {
    apiKeyError.value = 'Failed to copy to clipboard.'
  }
}

const handleCreateOAuthApp = async () => {
  if (!newAppName.value || !_nameRegex.test(newAppName.value)) {
    toastStore.showToast('Name must be 2-32 lowercase letters, numbers or hyphens, and start with a letter.', 'error')
    return
  }
  const uris = newAppRedirectURIs.value.split('\n').map(u => u.trim()).filter(Boolean)
  if (uris.length === 0) {
    oauthAppError.value = 'At least one redirect URI is required.'
    return
  }

  isCreatingApp.value = true
  oauthAppMessage.value = ''
  oauthAppError.value = ''
  createdAppCredentials.value = null

  try {
    const data = await orgStore.createOAuthApplication(organizationId, {
      application: {
        name: newAppName.value.trim(),
        description: newAppDescription.value.trim(),
        redirectURIs: uris,
      }
    })
    createdAppCredentials.value = {
      clientId: data.application?.clientId,
      clientSecret: data.clientSecret,
    }
    oauthAppMessage.value = 'OAuth Application created! Copy your client secret now — it won\'t be shown again.'
    newAppName.value = ''
    newAppDescription.value = ''
    newAppRedirectURIs.value = ''
    newAppSelectedScopes.value = []
    await fetchOAuthApps()
  } catch (err) {
    oauthAppError.value = err.response?.data?.error?.message || 'Failed to create OAuth application.'
  } finally {
    isCreatingApp.value = false
  }
}

const handleCopyClientSecret = async () => {
  if (!createdAppCredentials.value?.clientSecret) return
  try {
    await navigator.clipboard.writeText(createdAppCredentials.value.clientSecret)
    oauthAppMessage.value = 'Client secret copied!'
    setTimeout(() => {
      createdAppCredentials.value = null
      oauthAppMessage.value = ''
    }, 1000)
  } catch {
    oauthAppError.value = 'Failed to copy to clipboard.'
  }
}

const handleDeleteOAuthApp = (app) => {
  pendingDelete.value = { type: 'oauthApp', app }
}

const handleConfirmDelete = async () => {
  const target = pendingDelete.value
  pendingDelete.value = null

  if (target?.type === 'org') {
    isDeleting.value = true
    try {
      await orgStore.deleteOrganization(organizationId)
      toastStore.showToast('Organization deleted.')
      router.push('/organizations')
    } catch (err) {
      toastStore.showToast(err.response?.data?.error?.message || 'Failed to delete organization.', 'error')
    } finally {
      isDeleting.value = false
    }
  } else if (target?.type === 'oauthApp') {
    deletingAppId.value = target.app.id
    try {
      await orgStore.deleteOAuthApplication(organizationId, target.app.id)
      oauthApps.value = oauthApps.value.filter(a => a.id !== target.app.id)
      toastStore.showToast(`"${target.app.name}" deleted.`)
    } catch (err) {
      toastStore.showToast(err.response?.data?.error?.message || 'Failed to delete application.', 'error')
    } finally {
      deletingAppId.value = null
    }
  }
}

const handleDelete = async () => {
  if (!isDeleteAcknowledged.value) return
  pendingDelete.value = { type: 'org' }
}
</script>

<template>
  <div class="space-y-12">
    <div class="border-b border-b-gray-100 pb-4">
      <h1 class="text-xl text-gray-900">General organization settings</h1>
      <p class="text-md font-light text-gray-500 mt-1">Manage general settings for organization</p>
    </div>

    <div class="bg-white rounded-xl border border-gray-100 shadow-sm">
      <div class="px-6 py-5 border-b border-gray-100">
        <h2 class="text-lg text-gray-900">Organization</h2>
      </div>
      <div class="p-6 space-y-6">
        <div>
          <label class="block text-sm font-bold text-gray-900 mb-1">Identifier</label>
          <p class="text-xs text-gray-500 mb-2">Identifier is a generated unique identifier for this organization</p>
          <input type="text" :value="organizationId" disabled
            class="w-full bg-white border border-gray-200 text-gray-400 text-sm rounded-md block p-2.5 cursor-not-allowed">
        </div>

        <div>
          <label class="block text-sm font-bold text-gray-900 mb-1">Name</label>
          <p class="text-xs text-gray-500 mb-2">The organization name specified in here is displayed to all
            members/users (min 2 chars).</p>
          <input type="text" v-model="editableName"
            class="w-full border border-gray-300 text-gray-900 text-sm rounded-md block p-2.5 focus:ring-1 focus:ring-black focus:border-black"
            placeholder="Organization Name">
        </div>

        <div v-if="saveMessage" class="text-green-600 text-sm font-medium">{{ saveMessage }}</div>
        <div v-if="saveError" class="text-red-600 text-sm font-medium">{{ saveError }}</div>
      </div>

      <div class="px-6 py-4 bg-gray-50 text-right rounded-b-xl border-t border-gray-100">
        <button @click="handleSave" :disabled="!isNameValid || isSaving"
          class="bg-black text-white px-8 py-2 rounded-md text-sm font-bold hover:bg-gray-800 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 w-full sm:w-auto sm:ml-auto">
          <svg v-if="isSaving" class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none"
            viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
            </path>
          </svg>
          {{ isSaving ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </div>

    <div class="bg-white rounded-xl border border-gray-100 shadow-sm">
      <div class="px-6 py-5 border-b border-gray-100">
        <h2 class="text-lg text-gray-900">API Keys</h2>
        <p class="text-sm text-gray-500 mt-1">Create new API keys for accessing resources programmatically.</p>
      </div>

      <div v-if="generatedKey" class="p-6 space-y-6">
        <div class="bg-green-50 border border-green-200 rounded-md p-4">
          <div class="flex items-start">
            <div class="shrink-0">
              <svg class="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-green-800">API Key Generated</h3>
              <div class="mt-2 text-sm text-green-700">
                <p>Please copy your access token now. It will not be shown again.</p>
              </div>
              <div class="mt-4">
                <div class="flex items-center gap-2">
                  <code
                    class="bg-white px-3 py-2 rounded border border-green-300 block w-full text-xs font-mono break-all">{{ generatedKey.accessToken }}</code>
                  <button @click="handleCopyKey"
                    class="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-bold whitespace-nowrap">
                    Copy
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="apiKeyMessage" class="text-green-600 text-sm font-medium text-center">{{ apiKeyMessage }}</div>
      </div>

      <div v-else class="p-6 space-y-6">
        <div>
          <label class="block text-sm font-bold text-gray-900 mb-2">Expiration</label>
          <div class="flex rounded-sm " role="group">
            <button v-for="opt in expiryOptions" :key="opt" @click="selectedExpiry = opt" :class="[
              selectedExpiry === opt ? 'bg-black text-white z-10' : 'bg-white text-gray-700 hover:text-gray-900 hover:bg-gray-50',
              'px-4 py-2 text-xs font-medium border border-gray-200 first:rounded-l-sm last:rounded-r-sm -ml-px first:ml-0 focus:z-10 focus:ring-1 focus:ring-black'
            ]">
              {{ opt }}
            </button>
          </div>
        </div>

        <div>
          <label class="block text-sm font-bold text-gray-900 mb-3">Scopes</label>
          <div class="space-y-4">
            <div v-for="group in scopeGroups" :key="group.label"
              class="flex flex-col sm:flex-row sm:items-start border-b border-gray-100 last:border-0 pb-4 last:pb-0">
              <div class="w-32 shrink-0 pt-1">
                <span class="text-sm font-medium text-gray-700">{{ group.label }}</span>
              </div>
              <div class="flex-1 flex flex-wrap gap-3">
                <label v-for="action in group.actions" :key="action" class="inline-flex items-center cursor-pointer">
                  <input type="checkbox" :checked="selectedScopes.includes(`${group.prefix}:${action}`)"
                    @change="toggleScope(`${group.prefix}:${action}`)"
                    class="rounded border-gray-300 text-black shadow-sm focus:border-black focus:ring-black h-4 w-4">
                  <span class="ml-2 text-sm text-gray-600 capitalize">{{ action.replace('_', ' ') }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div v-if="apiKeyMessage" class="text-green-600 text-sm font-medium">{{ apiKeyMessage }}</div>
        <div v-if="apiKeyError" class="text-red-600 text-sm font-medium">{{ apiKeyError }}</div>
      </div>

      <div v-if="!generatedKey" class="px-6 py-4 bg-gray-50 text-right rounded-b-xl border-t border-gray-100">
        <button @click="handleCreateKey" :disabled="isCreatingKey || selectedScopes.length === 0"
          class="bg-black text-white px-8 py-2 rounded-md text-sm font-bold hover:bg-gray-800 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 w-full sm:w-auto sm:ml-auto">
          <svg v-if="isCreatingKey" class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg"
            fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
            </path>
          </svg>
          Generate Key
        </button>
      </div>
    </div>

    <!-- OAuth Applications -->
    <div class="bg-white rounded-xl border border-gray-100 shadow-sm">
      <div class="px-6 py-5 border-b border-gray-100">
        <h2 class="text-lg text-gray-900">OAuth Applications</h2>
        <p class="text-sm text-gray-500 mt-1">Register OAuth 2.0 applications to enable third-party services to authenticate on behalf of your organization members.</p>
      </div>

      <!-- Created credentials banner -->
      <div v-if="createdAppCredentials" class="p-6 space-y-4">
        <div class="bg-green-50 border border-green-200 rounded-md p-4">
          <h3 class="text-sm font-medium text-green-800 mb-3">Application Created — Save your credentials</h3>
          <p class="text-xs text-green-700 mb-4">Your client secret will only be shown once. Copy and store it securely.</p>
          <div class="space-y-3">
            <div>
              <label class="block text-xs font-semibold text-green-800 mb-1">Client ID</label>
              <div class="flex items-center gap-2">
                <code class="bg-white px-3 py-2 rounded border border-green-300 block w-full text-xs font-mono break-all">{{ createdAppCredentials.clientId }}</code>
                <button @click="() => navigator.clipboard.writeText(createdAppCredentials.clientId)" class="bg-green-100 hover:bg-green-200 text-green-800 px-3 py-2 rounded text-xs font-bold whitespace-nowrap">Copy</button>
              </div>
            </div>
            <div>
              <label class="block text-xs font-semibold text-green-800 mb-1">Client Secret</label>
              <div class="flex items-center gap-2">
                <code class="bg-white px-3 py-2 rounded border border-green-300 block w-full text-xs font-mono break-all">{{ createdAppCredentials.clientSecret }}</code>
                <button @click="handleCopyClientSecret" class="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-xs font-bold whitespace-nowrap">Copy & Close</button>
              </div>
            </div>
          </div>
        </div>
        <div v-if="oauthAppMessage" class="text-green-600 text-sm font-medium">{{ oauthAppMessage }}</div>
      </div>

      <!-- Create form -->
      <div v-else class="p-6 space-y-5">
        <div>
          <label class="block text-sm font-bold text-gray-900 mb-1">
            App Name
            <span class="font-normal text-gray-400 ml-1">{{ newAppName.length }}/32 — lowercase, numbers, hyphens</span>
          </label>
          <input type="text" v-model="newAppName"
            @input="newAppName = newAppName.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 32)"
            placeholder="my-oauth-app" maxlength="32"
            class="w-full border border-gray-300 text-gray-900 text-sm rounded-md block p-2.5 focus:ring-1 focus:ring-black focus:border-black">
        </div>
        <div>
          <label class="block text-sm font-bold text-gray-900 mb-1">Description <span class="font-normal text-gray-400">(optional)</span></label>
          <input type="text" v-model="newAppDescription" placeholder="Short description of your application"
            class="w-full border border-gray-300 text-gray-900 text-sm rounded-md block p-2.5 focus:ring-1 focus:ring-black focus:border-black">
        </div>
        <div>
          <label class="block text-sm font-bold text-gray-900 mb-1">Redirect URIs</label>
          <p class="text-xs text-gray-500 mb-2">One URI per line. Must match exactly what your app requests.</p>
          <textarea v-model="newAppRedirectURIs" rows="3" placeholder="https://your-app.com/callback"
            class="w-full border border-gray-300 text-gray-900 text-sm rounded-md block p-2.5 focus:ring-1 focus:ring-black focus:border-black font-mono"></textarea>
        </div>
        <div v-if="oauthAppMessage" class="text-green-600 text-sm font-medium">{{ oauthAppMessage }}</div>
        <div v-if="oauthAppError" class="text-red-600 text-sm font-medium">{{ oauthAppError }}</div>
      </div>

      <div v-if="!createdAppCredentials" class="px-6 py-4 bg-gray-50 text-right rounded-b-xl border-t border-gray-100">
        <button @click="handleCreateOAuthApp" :disabled="isCreatingApp"
          class="bg-black text-white px-8 py-2 rounded-md text-sm font-bold hover:bg-gray-800 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 w-full sm:w-auto sm:ml-auto">
          <svg v-if="isCreatingApp" class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Create OAuth Application
        </button>
      </div>

      <!-- Existing apps list -->
      <div v-if="oauthApps.length > 0" class="border-t border-gray-100">
        <div class="px-6 py-4">
          <h3 class="text-sm font-semibold text-gray-700 mb-3">Existing Applications</h3>
          <div class="space-y-2">
            <div v-for="app in oauthApps" :key="app.id"
              class="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md px-4 py-3">
              <div>
                <p class="text-sm font-medium text-gray-900">{{ app.name }}</p>
                <p class="text-xs text-gray-500 font-mono mt-0.5">Client ID: {{ app.clientId }}</p>
                <p v-if="app.description" class="text-xs text-gray-400 mt-0.5">{{ app.description }}</p>
              </div>
              <button @click="handleDeleteOAuthApp(app)" :disabled="deletingAppId === app.id"
                class="ml-4 shrink-0 text-xs px-3 py-1.5 rounded border border-red-200 text-red-600 hover:bg-red-50 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5">
                <svg v-if="deletingAppId === app.id" class="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Danger Zone -->
    <div class="bg-white rounded-xl border border-gray-100 shadow-sm">
      <div class="px-6 py-5 border-b border-gray-100">
        <h2 class="text-lg text-red-600">Danger zone</h2>
      </div>
      <div class="p-6">
        <div class="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <input id="delete-org-checkbox" type="checkbox" v-model="isDeleteAcknowledged"
            class="mt-0.5 sm:mt-0 shrink-0 w-4 h-4 text-red-600 bg-white border-gray-300 rounded focus:ring-red-500 focus:ring-offset-0 cursor-pointer">
          <label for="delete-org-checkbox" class="ml-0 sm:ml-2 text-sm font-medium text-gray-900 cursor-pointer">
            I acknowledge that deleting this organization will permanently remove all its members and data. This action
            cannot be undone.
          </label>
        </div>
        <div v-if="deleteError" class="mt-2 text-red-600 text-sm font-medium">{{ deleteError }}</div>
      </div>
      <div class="px-6 py-4 bg-gray-50 text-right rounded-b-xl border-t border-gray-100">
        <button @click="handleDelete" :disabled="!isDeleteAcknowledged || isDeleting" :class="[
          !isDeleteAcknowledged || isDeleting
            ? 'bg-gray-300 cursor-not-allowed'
            : 'bg-red-600 hover:bg-red-700 cursor-pointer'
        ]" class="text-white px-6 py-2 rounded-md text-sm font-bold transition flex items-center justify-center gap-2 w-full sm:w-auto sm:ml-auto">
          <svg v-if="isDeleting" class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none"
            viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
            </path>
          </svg>
          {{ isDeleting ? 'Deleting...' : 'Delete organization' }}
        </button>
      </div>
    </div>
  </div>

  <DeleteModal
    :show="isDeleteModalVisible"
    :itemType="deleteModalItemType"
    :variableName="deleteModalItemName"
    @close="pendingDelete = null"
    @confirm="handleConfirmDelete"
  />
</template>